import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from '../../config/axiosConfig';
import { API_ENDPOINTS } from '../../config/api';
import Layout from '../../components/Layout';
import Toast from '../../components/Toast';

const STATUS_OPTIONS = [
  { value: 'pending', label: 'Pending' },
  { value: 'contacted', label: 'Contacted' },
  { value: 'converted', label: 'Converted' },
  { value: 'closed', label: 'Closed' },
];

function statusStyle(status) {
  switch (status) {
    case 'converted':
      return 'bg-green-50 text-green-800 border-green-200';
    case 'contacted':
      return 'bg-blue-50 text-blue-800 border-blue-200';
    case 'closed':
      return 'bg-stone-100 text-stone-700 border-stone-200';
    default:
      return 'bg-amber-50 text-amber-800 border-amber-200';
  }
}

export default function RestroLiteLeadsPage({ readOnlyEmployee = false }) {
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dateFilter, setDateFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [toast, setToast] = useState({ show: false, message: '', type: 'success' });
  const [updatingId, setUpdatingId] = useState(null);
  const [deletingId, setDeletingId] = useState(null);

  const showToast = (message, type = 'success') => {
    setToast({ show: true, message, type });
    setTimeout(() => setToast({ show: false, message: '', type: 'success' }), 3000);
  };

  const fetchLeads = useCallback(async () => {
    try {
      setLoading(true);
      const url = API_ENDPOINTS.RESTRO_LITE_LEADS_LIST(dateFilter, statusFilter);
      const { data } = await axios.get(url);
      setLeads(data.leads || []);
    } catch (err) {
      showToast(err.response?.data?.message || 'Failed to load leads', 'error');
      setLeads([]);
    } finally {
      setLoading(false);
    }
  }, [dateFilter, statusFilter]);

  useEffect(() => {
    fetchLeads();
  }, [fetchLeads]);

  const handleStatusChange = async (leadId, newStatus) => {
    setUpdatingId(leadId);
    try {
      await axios.patch(API_ENDPOINTS.RESTRO_LITE_LEAD_STATUS(leadId), { status: newStatus });
      setLeads((prev) => prev.map((l) => (l._id === leadId ? { ...l, status: newStatus } : l)));
      showToast('Status updated');
    } catch (err) {
      showToast(err.response?.data?.message || 'Failed to update status', 'error');
    } finally {
      setUpdatingId(null);
    }
  };

  const handleDelete = async (lead) => {
    if (!window.confirm(`Delete lead for "${lead.shopName}"?`)) return;
    setDeletingId(lead._id);
    try {
      await axios.delete(API_ENDPOINTS.RESTRO_LITE_LEAD_DELETE(lead._id));
      setLeads((prev) => prev.filter((l) => l._id !== lead._id));
      showToast('Lead deleted');
    } catch (err) {
      showToast(err.response?.data?.message || 'Failed to delete', 'error');
    } finally {
      setDeletingId(null);
    }
  };

  const formatDate = (date) =>
    date
      ? new Date(date).toLocaleString('en-IN', {
          day: '2-digit',
          month: 'short',
          year: 'numeric',
          hour: '2-digit',
          minute: '2-digit',
        })
      : '—';

  return (
    <Layout>
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-slate-50 p-4 sm:p-6">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4 mb-6">
            <div>
              <Link
                to="/superadmin"
                className="text-blue-600 hover:text-blue-800 text-sm font-medium mb-2 inline-block"
              >
                ← Back to Dashboard
              </Link>
              <h1 className="text-2xl sm:text-3xl font-bold text-gray-800">PocketBillingAI Leads</h1>
              <p className="text-gray-600 mt-1">
                Free trial sign-ups from the PocketBillingAI app login screen.
              </p>
            </div>
            <div className="flex flex-wrap gap-2">
              {['', 'today', 'week', 'month'].map((f) => (
                <button
                  key={f || 'all'}
                  type="button"
                  onClick={() => setDateFilter(f)}
                  className={`px-3 py-2 rounded-xl text-sm font-medium ${
                    dateFilter === f
                      ? 'bg-blue-600 text-white'
                      : 'bg-white text-gray-700 border border-gray-300'
                  }`}
                >
                  {f === '' ? 'All dates' : f === 'today' ? 'Today' : f === 'week' ? 'Week' : 'Month'}
                </button>
              ))}
            </div>
          </div>

          <div className="flex flex-wrap gap-2 mb-4">
            {['all', ...STATUS_OPTIONS.map((s) => s.value)].map((s) => (
              <button
                key={s}
                type="button"
                onClick={() => setStatusFilter(s)}
                className={`px-3 py-1.5 rounded-lg text-sm font-medium ${
                  statusFilter === s
                    ? 'bg-indigo-600 text-white'
                    : 'bg-white border border-gray-300 text-gray-700'
                }`}
              >
                {s === 'all' ? 'All status' : STATUS_OPTIONS.find((o) => o.value === s)?.label}
              </button>
            ))}
          </div>

          {loading ? (
            <div className="bg-white rounded-2xl shadow p-12 text-center text-gray-600">
              Loading leads…
            </div>
          ) : (
            <div className="bg-white rounded-2xl shadow overflow-hidden">
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">
                        Date
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">
                        Shop
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">
                        Owner
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">
                        Contact
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">
                        Location
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">
                        Status
                      </th>
                      <th className="px-4 py-3 text-right text-xs font-semibold text-gray-600 uppercase">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {leads.length === 0 ? (
                      <tr>
                        <td colSpan={7} className="px-4 py-10 text-center text-gray-500">
                          No leads found.
                        </td>
                      </tr>
                    ) : (
                      leads.map((lead) => (
                        <tr key={lead._id} className="hover:bg-gray-50">
                          <td className="px-4 py-3 text-sm text-gray-600 whitespace-nowrap">
                            {formatDate(lead.createdAt)}
                          </td>
                          <td className="px-4 py-3 text-sm font-medium text-gray-900">
                            {lead.shopName}
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-700">{lead.ownerName}</td>
                          <td className="px-4 py-3 text-sm">
                            {lead.email && (
                              <div>
                                <a
                                  href={`mailto:${lead.email}`}
                                  className="text-blue-600 hover:underline"
                                >
                                  {lead.email}
                                </a>
                              </div>
                            )}
                            <a
                              href={`tel:${lead.contactNo}`}
                              className="text-blue-600 hover:underline tabular-nums"
                            >
                              {lead.contactNo}
                            </a>
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-600">
                            {lead.city}, {lead.state}
                            {lead.pincode ? ` · ${lead.pincode}` : ''}
                          </td>
                          <td className="px-4 py-3">
                            {readOnlyEmployee ? (
                              <span
                                className={`inline-block text-sm font-medium rounded-lg px-2 py-1 border ${statusStyle(
                                  lead.status
                                )}`}
                              >
                                {STATUS_OPTIONS.find((o) => o.value === lead.status)?.label ||
                                  lead.status ||
                                  'Pending'}
                              </span>
                            ) : (
                              <select
                                value={lead.status || 'pending'}
                                disabled={updatingId === lead._id}
                                onChange={(e) => handleStatusChange(lead._id, e.target.value)}
                                className={`text-sm font-medium rounded-lg px-2 py-1.5 border ${statusStyle(
                                  lead.status
                                )}`}
                              >
                                {STATUS_OPTIONS.map((o) => (
                                  <option key={o.value} value={o.value}>
                                    {o.label}
                                  </option>
                                ))}
                              </select>
                            )}
                          </td>
                          <td className="px-4 py-3 text-right">
                            {!readOnlyEmployee && (
                              <button
                                type="button"
                                disabled={deletingId === lead._id}
                                onClick={() => handleDelete(lead)}
                                className="text-sm text-red-600 hover:underline disabled:opacity-50"
                              >
                                Delete
                              </button>
                            )}
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      </div>
      <Toast show={toast.show} message={toast.message} type={toast.type} />
    </Layout>
  );
}
